import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { members } from '@/constants/portfolio'
import { useFrame } from '@/hooks/use-frame'
import { audioEngine } from '@/lib/audio/engine'
import { scrollToY } from '@/lib/scroll-to'
import { clamp, documentTop } from '@/lib/motion'
import { useExperience } from '@/components/generals/experience-provider'
import MemberDetail from '@/components/landing/member-detail'

const PANEL_ID = 'member-panel'

const tabId = (id: string): string => `member-tab-${id}`

// Persona-style select screen. On desktop the section is pinned and scrolling advances
// the selected member; the list, arrow keys and number keys jump straight to one.
const MembersSection: FC = (): ReactNode => {
    const { pinned } = useExperience()
    const [index, setIndex] = useState(0)
    const indexRef = useRef(0)
    const wrapRef = useRef<HTMLElement>(null)
    const barRef = useRef<HTMLSpanElement>(null)
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const measure = useRef({ at: -1, top: 0, travel: 1 })

    useFrame(state => {
        const wrap = wrapRef.current
        if (!wrap) return

        if (state.time - measure.current.at > 0.5) {
            measure.current = {
                at: state.time,
                top: documentTop(wrap),
                travel: Math.max(1, wrap.offsetHeight - state.viewportH)
            }
        }

        const { top, travel } = measure.current
        const p = clamp((state.rawScrollY - top) / travel)
        const smooth = clamp((state.scrollY - top) / travel)

        if (barRef.current) barRef.current.style.transform = `scaleX(${smooth})`

        if (state.rawScrollY < top - state.viewportH || state.rawScrollY > top + travel + state.viewportH) return

        const next = Math.min(members.length - 1, Math.floor(p * members.length))

        if (next !== indexRef.current) {
            indexRef.current = next
            setIndex(next)
            audioEngine.cue('move')
        }
    }, pinned)

    const jumpTo = (next: number, focus = false) => {
        const target = (next + members.length) % members.length

        if (target !== indexRef.current) audioEngine.cue('move')

        indexRef.current = target
        setIndex(target)

        if (pinned && wrapRef.current) {
            const wrap = wrapRef.current
            const top = documentTop(wrap)
            const travel = Math.max(1, wrap.offsetHeight - window.innerHeight)
            scrollToY(top + travel * ((target + 0.5) / members.length), true)
        }

        if (focus) tabRefs.current[target]?.focus()
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const keys: Record<string, number> = {
            ArrowDown: index + 1,
            ArrowRight: index + 1,
            ArrowUp: index - 1,
            ArrowLeft: index - 1,
            Home: 0,
            End: members.length - 1
        }

        if (event.key in keys) {
            event.preventDefault()
            jumpTo(keys[event.key], true)
            return
        }

        const number = Number(event.key)

        if (Number.isInteger(number) && number >= 1 && number <= members.length) {
            event.preventDefault()
            jumpTo(number - 1, true)
        }
    }

    const member = members[index]

    const tabs = (
        <div
            role='tablist'
            aria-label='Band members'
            aria-orientation={pinned ? 'vertical' : 'horizontal'}
            onKeyDown={onKeyDown}
            className={cn(
                pinned
                    ? 'mt-8 flex flex-col gap-1'
                    : 'no-scrollbar -mx-5 mt-8 flex snap-x gap-3 overflow-x-auto px-5 pb-3 xs:-mx-8 xs:px-8'
            )}
        >
            {members.map((item, position) => {
                const selected = position === index

                return (
                    <button
                        key={item.id}
                        ref={element => {
                            tabRefs.current[position] = element
                        }}
                        id={tabId(item.id)}
                        type='button'
                        role='tab'
                        aria-selected={selected}
                        aria-controls={PANEL_ID}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => {
                            audioEngine.cue('confirm')
                            jumpTo(position)
                        }}
                        className={cn(
                            'group relative flex shrink-0 snap-start items-center gap-3 text-left outline-offset-4',
                            pinned ? 'min-h-[clamp(3.6rem,9vh,5.4rem)] pl-4 pr-6' : 'min-h-12 px-4'
                        )}
                    >
                        <span
                            aria-hidden='true'
                            className={cn(
                                'absolute inset-0 origin-left translate-x-2 translate-y-1.5 -skew-x-12 bg-bandana transition-transform duration-200 ease-out',
                                selected ? 'scale-x-100' : 'scale-x-0'
                            )}
                        />
                        <span
                            aria-hidden='true'
                            className={cn(
                                'absolute inset-0 origin-left -skew-x-12 transition-transform duration-200 ease-out',
                                selected ? 'scale-x-100 bg-ink' : 'scale-x-0 bg-ink/10 group-hover:scale-x-100'
                            )}
                        />
                        <span className={cn('mono-label relative w-7 text-[11px]', selected ? 'text-paper/70' : 'text-ink-soft')}>
                            {String(item.order).padStart(2, '0')}
                        </span>
                        <span
                            className={cn(
                                'display-type menu-skew relative block origin-left transition-colors',
                                pinned ? 'text-[clamp(2.2rem,4.6vw,4.6rem)]' : 'text-[1.6rem]',
                                selected ? 'text-paper' : 'text-ink'
                            )}
                        >
                            {item.shortName}
                        </span>
                        {pinned && (
                            <span className={cn('mono-label relative ml-auto hidden pl-4 text-[10px] wide:block', selected ? 'text-paper/80' : 'text-ink-soft')}>
                                {item.primaryRole}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )

    const heading = (
        <div>
            <p className='mono-label text-[11px] text-ink-soft'>02 / Choose your player</p>
            <h2 id='members-title' className='display-type mt-3 text-[clamp(3rem,6vw,5.6rem)]'>Members</h2>
            <p className='mt-3 max-w-[36ch] text-[16px] leading-[1.55] text-ink-soft'>
                Four players, four sets of influences. Pick one to read their story and hear their part.
            </p>
        </div>
    )

    if (!pinned) {
        return (
            <section id='members' aria-labelledby='members-title' className='relative overflow-hidden bg-paper px-5 py-20 xs:px-8 tab:px-12 tab:py-28'>
                {heading}
                {tabs}
                <div className='mt-8'>
                    <MemberDetail member={member} compact panelId={PANEL_ID} labelledBy={tabId(member.id)} />
                </div>
            </section>
        )
    }

    return (
        <section
            ref={wrapRef}
            id='members'
            data-pin-wrapper='true'
            aria-labelledby='members-title'
            className='relative h-[460vh] bg-paper'
        >
            <div className='sticky top-0 h-[100svh] overflow-hidden'>
                <span
                    aria-hidden='true'
                    className='display-type pointer-events-none absolute -right-[4vh] top-1/2 block -translate-y-1/2 rotate-90 text-[44vh] leading-[0.75] text-ink/[0.05]'
                >
                    {String(member.order).padStart(2, '0')}
                </span>

                <div className='relative mx-auto grid h-full max-w-[1680px] grid-cols-[minmax(300px,30%)_1fr] gap-10 px-12 pb-[4vh] pt-[92px] wide:gap-16'>
                    <div className='flex flex-col justify-center'>
                        {heading}
                        {tabs}

                        <div className='mt-8 flex items-center gap-4'>
                            <span className='relative block h-[3px] w-32 bg-ink/15' aria-hidden='true'>
                                <span ref={barRef} className='absolute inset-0 origin-left bg-ink' />
                            </span>
                            <span className='mono-label text-[10px] text-ink-soft'>Scroll, ↑ ↓ or 1 to 4</span>
                        </div>
                    </div>

                    <MemberDetail member={member} panelId={PANEL_ID} labelledBy={tabId(member.id)} />
                </div>
            </div>
        </section>
    )
}

export default MembersSection
